/**
 * VoiceInput - Speech-to-Text input component for MarIA
 *
 * Uses the Web Speech API (SpeechRecognition) to convert voice to text.
 * The transcribed text is passed to the parent component for sending via
 * the existing /run_sse endpoint - NO changes to backend required.
 *
 * Browser support:
 * - Chrome/Edge: Full support (uses Google's speech recognition)
 * - Safari: Partial support (14.1+)
 * - Firefox: Not supported
 *
 * Privacy note: Audio is processed by browser's speech service (Google for Chrome).
 * No audio is stored; only the transcribed text is sent to MarIA.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Web_Speech_API
 * @see Issue #XX - Voice Input Feature
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/button';

interface VoiceInputProps {
  /**
   * Called when speech recognition produces a final transcript.
   * The parent component should use this text as user input.
   */
  onTranscript: (text: string) => void;

  /**
   * Called with interim results while user is speaking.
   * Useful for showing "preview" of what's being recognized.
   */
  onInterimResult?: (text: string) => void;

  /**
   * Disable the voice input button
   */
  disabled?: boolean;

  /**
   * Auto-send the transcript (call onTranscript immediately when speech ends)
   * If false, user must manually send after seeing the transcript.
   * @default true
   */
  autoSend?: boolean;

  /**
   * Language for speech recognition (BCP-47 format)
   * @default 'es-ES'
   */
  language?: string;
}

/**
 * Check if Web Speech API is supported in the current browser
 */
function isSpeechRecognitionSupported(): boolean {
  return !!(
    typeof window !== 'undefined' &&
    (window.SpeechRecognition || window.webkitSpeechRecognition)
  );
}

/**
 * Get the SpeechRecognition constructor (with vendor prefix fallback)
 */
function getSpeechRecognition(): typeof SpeechRecognition | null {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

export function VoiceInput({
  onTranscript,
  onInterimResult,
  disabled = false,
  autoSend = true,
  language = 'es-ES',
}: VoiceInputProps) {
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const finalTranscriptRef = useRef<string>('');

  // Check browser support on mount
  useEffect(() => {
    setIsSupported(isSpeechRecognitionSupported());
  }, []);

  /**
   * Start listening for speech
   */
  const startListening = useCallback(() => {
    const SpeechRecognitionClass = getSpeechRecognition();

    if (!SpeechRecognitionClass) {
      setErrorMessage('Tu navegador no soporta reconocimiento de voz. Usa Chrome o Edge.');
      return;
    }

    // Clear any previous error
    setErrorMessage(null);
    finalTranscriptRef.current = '';

    const recognition = new SpeechRecognitionClass();

    // Configuration
    recognition.lang = language;
    recognition.continuous = false; // Stop after one utterance
    recognition.interimResults = true; // Show results while speaking
    recognition.maxAlternatives = 1; // Only need the best result

    // Event handlers
    recognition.onstart = () => {
      setIsListening(true);
    };

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let interimTranscript = '';
      let finalTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const transcript = result[0].transcript;

        if (result.isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      // Update interim results for preview
      if (interimTranscript && onInterimResult) {
        onInterimResult(interimTranscript);
      }

      // Store final transcript
      if (finalTranscript) {
        finalTranscriptRef.current += finalTranscript;
      }
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      console.error('Speech recognition error:', event.error);

      let message: string;
      switch (event.error) {
        case 'no-speech':
          message = 'No se detectó voz. Intenta de nuevo.';
          break;
        case 'audio-capture':
          message = 'No se pudo acceder al micrófono. Verifica los permisos.';
          break;
        case 'not-allowed':
          message = 'Permiso de micrófono denegado. Habilítalo en la configuración del navegador.';
          break;
        case 'network':
          message = 'Error de red. Verifica tu conexión a internet.';
          break;
        case 'aborted':
          // User cancelled, no error message needed
          message = '';
          break;
        default:
          message = `Error de reconocimiento: ${event.error}`;
      }

      if (message) {
        setErrorMessage(message);
      }
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);

      // Clear interim results
      if (onInterimResult) {
        onInterimResult('');
      }

      // Send final transcript if we have one
      const finalText = finalTranscriptRef.current.trim();
      if (finalText) {
        if (autoSend) {
          onTranscript(finalText);
        } else {
          // Just pass the text, parent decides what to do
          onTranscript(finalText);
        }
      }
    };

    // Store reference and start
    recognitionRef.current = recognition;

    try {
      recognition.start();
    } catch (err) {
      console.error('Failed to start speech recognition:', err);
      setErrorMessage('No se pudo iniciar el reconocimiento de voz.');
    }
  }, [language, onTranscript, onInterimResult, autoSend]);

  /**
   * Stop listening for speech
   */
  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setIsListening(false);
  }, []);

  /**
   * Toggle listening state
   */
  const toggleListening = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [isListening, startListening, stopListening]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);

  // Don't render if not supported
  if (!isSupported) {
    return null;
  }

  return (
    <div className="flex items-center gap-2">
      <Button
        type="button"
        variant={isListening ? 'destructive' : 'outline'}
        size="icon"
        onClick={toggleListening}
        disabled={disabled}
        title={isListening ? 'Detener grabación' : 'Hablar con MarIA'}
        aria-label={isListening ? 'Detener grabación de voz' : 'Iniciar grabación de voz'}
        className={`relative transition-all ${
          isListening ? 'animate-pulse ring-2 ring-red-400 ring-offset-2' : ''
        }`}
      >
        {isListening ? (
          // Stop icon
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="w-5 h-5"
          >
            <path
              fillRule="evenodd"
              d="M4.5 7.5a3 3 0 013-3h9a3 3 0 013 3v9a3 3 0 01-3 3h-9a3 3 0 01-3-3v-9z"
              clipRule="evenodd"
            />
          </svg>
        ) : (
          // Microphone icon
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="w-5 h-5"
          >
            <path d="M8.25 4.5a3.75 3.75 0 117.5 0v8.25a3.75 3.75 0 11-7.5 0V4.5z" />
            <path d="M6 10.5a.75.75 0 01.75.75v1.5a5.25 5.25 0 1010.5 0v-1.5a.75.75 0 011.5 0v1.5a6.751 6.751 0 01-6 6.709v2.291h3a.75.75 0 010 1.5h-7.5a.75.75 0 010-1.5h3v-2.291a6.751 6.751 0 01-6-6.709v-1.5A.75.75 0 016 10.5z" />
          </svg>
        )}
      </Button>

      {/* Error message */}
      {errorMessage && (
        <span className="text-sm text-red-500" role="alert">
          {errorMessage}
        </span>
      )}
    </div>
  );
}

export default VoiceInput;